import { apiClient } from "@/lib/apiClient";
import { KYCStatusResponse, KYCSubmissionResponse } from "@/types/models/kyc";

export const kycService = {
  /**
   * Get KYC status for a store
   */
  async getKycStatus(): Promise<KYCStatusResponse> {
    try {
      const response = await apiClient.get<KYCStatusResponse>(
        "/api/v1/admin/:storeId/kyc",
      );
      return response.data;
    } catch (error: unknown) {
      // Check if the error has response data (API returned structured response)
      const axiosError = error as { response?: { data?: KYCStatusResponse } };
      if (axiosError.response?.data) {
        // If the server returned a structured response, use it even if HTTP status indicates error
        return axiosError.response.data;
      }
      throw error;
    }
  },

  /**
   * Submit business details (Step 1)
   * POST /api/v1/admin/{storeId}/kyc/business-details
   */
  async submitBusinessDetails(
    formData: FormData,
  ): Promise<KYCSubmissionResponse> {
    try {
      const response = await apiClient.post<KYCSubmissionResponse>(
        "/api/v1/admin/:storeId/kyc/business-details",
        formData,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Submit personal details (Step 2)
   * POST /api/v1/admin/{storeId}/kyc/personal-details
   */
  async submitPersonalDetails(
    formData: FormData,
  ): Promise<KYCSubmissionResponse> {
    try {
      const response = await apiClient.post<KYCSubmissionResponse>(
        "/api/v1/admin/:storeId/kyc/personal-details",
        formData,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Upload documents (Step 3)
   * POST /api/v1/admin/{storeId}/kyc/document-upload?update={isUpdate}
   */
  async uploadDocuments(
    formData: FormData,
    isUpdate: boolean = false,
  ): Promise<KYCSubmissionResponse> {
    try {
      const url = `/api/v1/admin/:storeId/kyc/document-upload${
        isUpdate ? "?update=true" : ""
      }`;
      const response = await apiClient.post<KYCSubmissionResponse>(
        url,
        formData,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Upload address proof (Step 4)
   * POST /api/v1/admin/{storeId}/kyc/address-verification?update={isUpdate}
   */
  async uploadAddressProof(
    formData: FormData,
    isUpdate: boolean = false,
  ): Promise<KYCSubmissionResponse> {
    try {
      const url = `/api/v1/admin/:storeId/kyc/address-verification${
        isUpdate ? "?update=true" : ""
      }`;
      const response = await apiClient.post<KYCSubmissionResponse>(
        url,
        formData,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Upload video KYC (Step 5)
   * POST /api/v1/admin/{storeId}/kyc/video-verification?update={isUpdate}
   */
  async uploadVideoKyc(
    formData: FormData,
    isUpdate: boolean = false,
  ): Promise<KYCSubmissionResponse> {
    try {
      const url = `/api/v1/admin/:storeId/kyc/video-verification${
        isUpdate ? "?update=true" : ""
      }`;
      const response = await apiClient.post<KYCSubmissionResponse>(
        url,
        formData,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update specific KYC fields using PUT (for resubmissions)
   * PUT /api/v1/admin/{storeId}/kyc
   */
  async updateKycFields(
    fields: Record<string, unknown>,
  ): Promise<KYCSubmissionResponse> {
    try {
      const formData = new FormData();

      // Add all provided fields to FormData
      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof File || typeof value === "string") {
            formData.append(key, value);
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const response = await apiClient.put<KYCSubmissionResponse>(
        "/api/v1/admin/:storeId/kyc",
        formData,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Submit complete KYC with all fields at once
   * POST /api/v1/admin/{storeId}/kyc
   */
  async submitCompleteKyc(
    formData: FormData,
  ): Promise<KYCSubmissionResponse> {
    try {
      const response = await apiClient.post<KYCSubmissionResponse>(
        "/api/v1/admin/:storeId/kyc",
        formData,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// File validation utilities
export const fileValidation = {
  /**
   * Validate file size
   */
  validateFileSize(file: File, maxSize: number): boolean {
    return file.size <= maxSize;
  },

  /**
   * Validate file type
   */
  validateFileType(file: File, allowedTypes: string[]): boolean {
    return allowedTypes.includes(file.type);
  },

  /**
   * Validate image file
   */
  validateImageFile(
    file: File,
    maxSize: number = 2 * 1024 * 1024,
  ): { isValid: boolean; error?: string } {
    const allowedTypes = ["image/jpeg", "image/png"];

    if (!this.validateFileType(file, allowedTypes)) {
      return { isValid: false, error: "Please upload a JPEG or PNG image" };
    }

    if (!this.validateFileSize(file, maxSize)) {
      return {
        isValid: false,
        error: `File size must be less than ${maxSize / (1024 * 1024)}MB`,
      };
    }

    return { isValid: true };
  },

  /**
   * Validate video file
   */
  validateVideoFile(
    file: File,
    maxSize: number = 50 * 1024 * 1024,
  ): { isValid: boolean; error?: string } {
    const allowedTypes = ["video/mp4", "video/webm", "video/quicktime"];

    if (!this.validateFileType(file, allowedTypes)) {
      return {
        isValid: false,
        error: "Please upload a MP4, WebM, or QuickTime video",
      };
    }

    if (!this.validateFileSize(file, maxSize)) {
      return {
        isValid: false,
        error: `File size must be less than ${maxSize / (1024 * 1024)}MB`,
      };
    }

    return { isValid: true };
  },
};

// Utility functions for form data preparation
export const formDataUtils = {
  /**
   * Create FormData for business details
   */
  createBusinessDetailsFormData(data: {
    business_name: string;
    business_logo?: File | null;
    business_email: string;
    business_phone_number: string;
    address_line_one: string;
    address_line_two?: string;
    city: string;
    state: string;
    postal_code: string;
  }): FormData {
    const formData = new FormData();

    formData.append("business_name", data.business_name);
    formData.append("business_email", data.business_email);
    formData.append("business_phone_number", data.business_phone_number);
    formData.append("address_line_one", data.address_line_one);
    if (data.address_line_two) {
      formData.append("address_line_two", data.address_line_two);
    }
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("postal_code", data.postal_code);

    if (data.business_logo) {
      formData.append("business_logo", data.business_logo);
    }

    return formData;
  },

  /**
   * Create FormData for personal details
   */
  createPersonalDetailsFormData(data: { gst_number: string }): FormData {
    const formData = new FormData();
    formData.append("gst_number", data.gst_number);
    return formData;
  },

  /**
   * Create FormData for document upload
   */
  createDocumentUploadFormData(data: {
    aadhaar_document_front: File;
    aadhaar_document_back: File;
    pan_document: File;
  }): FormData {
    const formData = new FormData();

    formData.append("aadhaar_document_front", data.aadhaar_document_front);
    formData.append("aadhaar_document_back", data.aadhaar_document_back);
    formData.append("pan_document", data.pan_document);

    return formData;
  },

  /**
   * Create FormData for address verification
   */
  createAddressVerificationFormData(data: {
    address_proof_document: File;
  }): FormData {
    const formData = new FormData();
    formData.append("address_proof_document", data.address_proof_document);
    return formData;
  },

  /**
   * Create FormData for video verification
   */
  createVideoVerificationFormData(data: { video_kyc_file: File }): FormData {
    const formData = new FormData();
    formData.append("video_kyc_file", data.video_kyc_file);
    return formData;
  },
};

export default kycService;
